import React, { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { CustomThemePreset, getThemePreset } from './themePresets';

export type ThemeMode = 'light' | 'dark' | 'auto' | 'custom';

const THEME_STORAGE_KEY = 'tasking-theme';
const CUSTOM_THEME_ID_KEY = 'tasking-custom-theme-id';
const CUSTOM_THEME_VARS_KEY = 'tasking-custom-theme-vars';

// Event mechanism to sync standalone functions with React Context
type ThemeChangeListener = (mode: ThemeMode, effectiveTheme: 'light' | 'dark') => void;
const listeners = new Set<ThemeChangeListener>();

// Track the actual applied theme (light or dark), separate from mode setting
// Initialize from body class if available (SSR-safe)
let currentEffectiveTheme: 'light' | 'dark' =
  typeof document !== 'undefined'
    ? document.documentElement.getAttribute('data-theme') === 'light' ||
      document.body?.getAttribute('data-theme') === 'light' ||
      document.documentElement.classList.contains('light') ||
      document.body?.classList?.contains('light')
      ? 'light'
      : 'dark'
    : 'dark';

function notifyThemeChange() {
  const mode = getCurrentThemeMode();
  listeners.forEach((listener) => listener(mode, currentEffectiveTheme));
}

function subscribeToThemeChange(listener: ThemeChangeListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function updateTheme(darkPreferred: boolean): void {
  const root = document.documentElement;
  const body = document.body;
  root.classList.remove('light', 'dark');
  body.classList.remove('light', 'dark');
  if (darkPreferred) {
    root.classList.add('dark');
    root.setAttribute('data-theme', 'dark');
    body.classList.add('dark');
    body.setAttribute('data-theme', 'dark');
    currentEffectiveTheme = 'dark';
  } else {
    root.classList.add('light');
    root.setAttribute('data-theme', 'light');
    body.classList.add('light');
    body.setAttribute('data-theme', 'light');
    currentEffectiveTheme = 'light';
  }
}

function saveThemePreference(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (e) {
    // Ignore localStorage errors
  }
}

function loadThemePreference(): ThemeMode | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto' || stored === 'custom') {
      return stored;
    }
    // Backward compatibility for older provider key.
    const legacy = localStorage.getItem('theme');
    if (legacy === 'light' || legacy === 'dark' || legacy === 'system') {
      const migrated: ThemeMode = legacy === 'system' ? 'auto' : legacy;
      localStorage.setItem(THEME_STORAGE_KEY, migrated);
      return migrated;
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
}

let currentMediaQuery: MediaQueryList | null = null;
let currentCustomThemeId: string | null = null;

/**
 * Bridge map: project token → Untitled UI semantic tokens.
 * When custom themes set project-level variables, these also need to flow
 * through to the Untitled UI design system so both old CSS-module components
 * and new Untitled UI components reflect the custom colours.
 */
const PROJECT_TO_UNTITLED_UI_MAP: Record<string, string[]> = {
  '--background': ['--color-bg-primary', '--color-bg-primary_alt'],
  '--foreground': ['--color-fg-primary', '--color-text-primary'],
  '--surface': ['--color-bg-secondary', '--color-bg-secondary_subtle'],
  '--card': ['--color-bg-tertiary'],
  '--border': ['--color-border-primary', '--color-border-secondary'],
  '--primary': ['--color-bg-brand-solid', '--color-fg-brand-primary'],
  '--primary-foreground': ['--color-text-primary_on-brand'],
  '--muted': ['--color-bg-quaternary'],
  '--muted-foreground': ['--color-fg-quaternary', '--color-text-tertiary'],
  '--error': ['--color-error-600', '--color-error-500'],
  '--warning': ['--color-warning-600', '--color-warning-500'],
  '--success': ['--color-success-600', '--color-success-500'],
  '--info': ['--color-blue-500'],
};

/** Track all custom properties we've set so clearCustomVariables can remove them */
let appliedCustomProperties: string[] = [];

/**
 * Apply CSS variable overrides from a custom theme onto :root.
 * Also bridges project tokens to Untitled UI semantic tokens so both
 * old and new component systems respond to custom theme colours.
 */
function applyCustomVariables(variables: Record<string, string>): void {
  const root = document.documentElement;
  const allProperties: string[] = [];

  for (const [key, value] of Object.entries(variables)) {
    root.style.setProperty(key, value);
    allProperties.push(key);

    // Bridge to Untitled UI semantic tokens
    const mappedTokens = PROJECT_TO_UNTITLED_UI_MAP[key];
    if (mappedTokens) {
      for (const token of mappedTokens) {
        root.style.setProperty(token, value);
        allProperties.push(token);
      }
    }
  }

  appliedCustomProperties = allProperties;
}

/**
 * Remove all custom CSS variable overrides from :root,
 * including any bridged Untitled UI tokens.
 */
function clearCustomVariables(): void {
  const root = document.documentElement;

  // Remove all properties we applied (project tokens + bridged Untitled UI tokens)
  for (const key of appliedCustomProperties) {
    root.style.removeProperty(key);
  }
  appliedCustomProperties = [];

  // Also clean up from saved vars (in case of page reload where appliedCustomProperties is empty)
  const savedVars = loadCustomThemeVars();
  if (savedVars) {
    for (const key of Object.keys(savedVars)) {
      root.style.removeProperty(key);
      // Also remove any bridged tokens
      const mappedTokens = PROJECT_TO_UNTITLED_UI_MAP[key];
      if (mappedTokens) {
        for (const token of mappedTokens) {
          root.style.removeProperty(token);
        }
      }
    }
  }

  currentCustomThemeId = null;
}

function saveCustomTheme(id: string, variables: Record<string, string>): void {
  try {
    localStorage.setItem(CUSTOM_THEME_ID_KEY, id);
    localStorage.setItem(CUSTOM_THEME_VARS_KEY, JSON.stringify(variables));
  } catch (e) {
    /* ignore */
  }
}

function clearSavedCustomTheme(): void {
  try {
    localStorage.removeItem(CUSTOM_THEME_ID_KEY);
    localStorage.removeItem(CUSTOM_THEME_VARS_KEY);
  } catch (e) {
    /* ignore */
  }
}

function loadCustomThemeId(): string | null {
  try {
    return localStorage.getItem(CUSTOM_THEME_ID_KEY);
  } catch (e) {
    return null;
  }
}

function loadCustomThemeVars(): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(CUSTOM_THEME_VARS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Get the currently active custom theme ID (null if not using a custom theme).
 */
export function getCustomThemeId(): string | null {
  return currentCustomThemeId;
}

/**
 * Get the current custom theme variables (null if not using a custom theme).
 */
export function getCustomThemeVars(): Record<string, string> | null {
  return loadCustomThemeVars();
}

/**
 * Switch to dark mode.
 */
export function switchToDarkMode(): void {
  // Clear any auto mode listener if present.
  if (currentMediaQuery) {
    currentMediaQuery.onchange = null;
    currentMediaQuery = null;
  }
  clearCustomVariables();
  clearSavedCustomTheme();
  updateTheme(true);
  saveThemePreference('dark');
  notifyThemeChange();
}

/**
 * Switch to light mode.
 */
export function switchToLightMode(): void {
  // Clear any auto mode listener if present.
  if (currentMediaQuery) {
    currentMediaQuery.onchange = null;
    currentMediaQuery = null;
  }
  clearCustomVariables();
  clearSavedCustomTheme();
  updateTheme(false);
  saveThemePreference('light');
  notifyThemeChange();
}

/**
 * Switch to auto mode. This function immediately applies the user's color scheme preference
 * and listens for system preference changes to update the theme automatically.
 */
export function switchToAutoMode(): void {
  if (currentMediaQuery) {
    currentMediaQuery.onchange = null;
  }
  clearCustomVariables();
  clearSavedCustomTheme();
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.onchange = (e: MediaQueryListEvent) => {
    updateTheme(e.matches);
  };
  currentMediaQuery = mediaQuery;
  updateTheme(mediaQuery.matches);
  saveThemePreference('auto');
  notifyThemeChange();
}

/**
 * Switch to a custom theme preset or user-defined variables.
 * Applies the base (dark/light) class and then overlays the custom CSS variables.
 */
export function switchToCustomTheme(
  themeOrId: CustomThemePreset | string,
  customVars?: Record<string, string>,
): void {
  // Clear any auto mode listener
  if (currentMediaQuery) {
    currentMediaQuery.onchange = null;
    currentMediaQuery = null;
  }

  let id: string;
  let base: 'dark' | 'light';
  let variables: Record<string, string>;

  if (typeof themeOrId === 'string') {
    // Look up preset by ID, or use custom vars
    const preset = getThemePreset(themeOrId);
    if (preset) {
      id = preset.id;
      base = preset.base;
      variables = preset.variables;
    } else if (customVars) {
      id = themeOrId;
      base =
        customVars['--background']?.includes('98%') ||
        customVars['--background']?.includes('96%') ||
        customVars['--background']?.includes('97%')
          ? 'light'
          : 'dark';
      variables = customVars;
    } else {
      return; // Unknown theme, do nothing
    }
  } else {
    id = themeOrId.id;
    base = themeOrId.base;
    variables = themeOrId.variables;
  }

  // Clear old custom vars before applying new ones
  clearCustomVariables();

  // Apply base dark/light class
  updateTheme(base === 'dark');

  // Apply custom CSS variable overrides
  applyCustomVariables(variables);

  // Persist
  currentCustomThemeId = id;
  saveThemePreference('custom');
  saveCustomTheme(id, variables);

  // Dispatch event for preview and other listeners
  window.dispatchEvent(
    new CustomEvent('tasking-custom-theme', { detail: { id, base, variables } }),
  );

  notifyThemeChange();
}

/**
 * Initialize theme from stored preference or default to dark.
 */
export function initializeTheme(): void {
  const stored = loadThemePreference();
  if (stored === 'custom') {
    const customId = loadCustomThemeId();
    const customVars = loadCustomThemeVars();
    if (customId && customVars) {
      switchToCustomTheme(customId, customVars);
    } else {
      // Fallback if custom data is missing
      switchToDarkMode();
    }
  } else if (stored === 'light') {
    switchToLightMode();
  } else if (stored === 'auto') {
    switchToAutoMode();
  } else {
    // Default to dark
    switchToDarkMode();
  }
}

/**
 * Returns the current theme mode:
 * - "auto" if auto mode is enabled,
 * - "dark" if the document body has the "dark" class,
 * - "light" otherwise.
 */
export function getCurrentThemeMode(): ThemeMode {
  if (currentCustomThemeId) {
    return 'custom';
  }
  if (currentMediaQuery) {
    return 'auto';
  }
  return document.body.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * Returns the currently applied theme (light or dark), regardless of mode setting.
 */
export function getEffectiveTheme(): 'light' | 'dark' {
  return currentEffectiveTheme;
}

// -- React Context & Provider --

interface ThemeModeContextValue {
  mode: ThemeMode;
  effectiveTheme: 'light' | 'dark';
  customThemeId: string | null;
  switchToDarkMode: () => void;
  switchToLightMode: () => void;
  switchToAutoMode: () => void;
  switchToCustomTheme: (
    themeOrId: CustomThemePreset | string,
    customVars?: Record<string, string>,
  ) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => getCurrentThemeMode());
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>(
    () => currentEffectiveTheme,
  );
  const [customThemeId, setCustomThemeId] = useState<string | null>(() => currentCustomThemeId);

  // Initialize theme from localStorage on mount and subscribe to changes
  useEffect(() => {
    // Set up subscription FIRST so we catch the theme change from initializeTheme
    const unsubscribe = subscribeToThemeChange((newMode, newEffective) => {
      setMode(newMode);
      setEffectiveTheme(newEffective);
      setCustomThemeId(currentCustomThemeId);
    });

    // Then initialize theme - this will trigger the subscription
    initializeTheme();

    // Also sync the state in case initializeTheme ran before subscription was ready
    setMode(getCurrentThemeMode());
    setEffectiveTheme(currentEffectiveTheme);

    return unsubscribe;
  }, []);

  const value = useMemo(
    () => ({
      mode,
      effectiveTheme,
      customThemeId,
      switchToDarkMode,
      switchToLightMode,
      switchToAutoMode,
      switchToCustomTheme,
    }),
    [mode, effectiveTheme, customThemeId],
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode(): ThemeModeContextValue {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
}
